import { useState } from "react";
import PageHero from "@/components/PageHero";
import CalendlyEmbed from "@/components/CalendlyEmbed";
import useSEO from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Phone, Mail, Globe, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SERVICE_OPTIONS = [
  "Performance Marketing",
  "SEO",
  "Social Media Marketing",
  "Content & Creative",
  "Website Development",
  "Marketing Automation",
  "Lead Generation",
  "Not sure — need consultation",
];

export default function Contact() {
  useSEO();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please enter your full name";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim() || form.message.trim().length < 5) e.message = "Tell us a bit more (min 5 chars)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      setSuccess(true);
      toast.success("Message sent! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title={<>Let's build something <span className="text-saavi-blue">great</span> together.</>}
        description="Whether you're launching a brand, scaling your business, or looking for more qualified leads — we're ready to help."
      />

      <section className="section-pad">
        <div className="container-saavi grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <div className="border border-border rounded-md p-7 bg-card">
              <div className="font-mono-overline text-saavi-blue mb-4">Direct Contact</div>
              <div className="space-y-4">
                <a href="tel:+917985005252" data-testid="contact-phone" className="flex items-center gap-3 text-lg font-semibold hover:text-saavi-blue transition-colors">
                  <Phone className="h-5 w-5 text-saavi-blue" /> +91 7985005252
                </a>
                <a href="mailto:hello@saavidigitex.com" data-testid="contact-email" className="flex items-center gap-3 text-lg font-semibold hover:text-saavi-red transition-colors">
                  <Mail className="h-5 w-5 text-saavi-red" /> hello@saavidigitex.com
                </a>
                <a href="https://www.saavidigitex.com" target="_blank" rel="noreferrer" className="flex items-center gap-3 text-lg font-semibold hover:text-saavi-green transition-colors">
                  <Globe className="h-5 w-5 text-saavi-green" /> www.saavidigitex.com
                </a>
              </div>
            </div>

            <div className="border border-border rounded-md p-7 bg-card">
              <div className="font-mono-overline text-saavi-green mb-3">Free Consultation</div>
              <h3 className="text-2xl">Book your free digital growth consultation today.</h3>
              <p className="mt-3 text-sm text-muted-foreground">A 30-minute call where we audit your current funnel and share 3 actionable opportunities.</p>
              <div className="mt-5 flex flex-wrap gap-2 text-xs">
                <span className="font-mono-overline px-2.5 py-1 rounded-md border border-border">No Obligation</span>
                <span className="font-mono-overline px-2.5 py-1 rounded-md border border-border">30 min</span>
                <span className="font-mono-overline px-2.5 py-1 rounded-md border border-border">Senior Strategist</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="border border-border rounded-md p-7 md:p-9 bg-card"
              noValidate
            >
              {success ? (
                <div data-testid="contact-success" className="py-12 text-center">
                  <CheckCircle2 className="h-12 w-12 text-saavi-green mx-auto" />
                  <h3 className="mt-5 text-2xl">Message received.</h3>
                  <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                    Thank you for reaching out to Saavi Digitex. A growth strategist will get back to you within 24 hours.
                  </p>
                  <Button onClick={() => setSuccess(false)} variant="outline" className="mt-6" data-testid="contact-send-another">
                    Send another message
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl md:text-3xl">Tell us about your project</h2>
                  <p className="mt-2 text-sm text-muted-foreground">We typically reply within 1 business day.</p>

                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-sm">Full Name *</Label>
                      <Input
                        id="name" data-testid="contact-input-name"
                        value={form.name} onChange={(e) => update("name", e.target.value)}
                        className="mt-1.5 h-11" placeholder="Your name"
                      />
                      {errors.name && <p className="mt-1 text-xs text-saavi-red">{errors.name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm">Email *</Label>
                      <Input
                        id="email" type="email" data-testid="contact-input-email"
                        value={form.email} onChange={(e) => update("email", e.target.value)}
                        className="mt-1.5 h-11" placeholder="you@company.com"
                      />
                      {errors.email && <p className="mt-1 text-xs text-saavi-red">{errors.email}</p>}
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm">Phone</Label>
                      <Input
                        id="phone" data-testid="contact-input-phone"
                        value={form.phone} onChange={(e) => update("phone", e.target.value)}
                        className="mt-1.5 h-11" placeholder="+91 ..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-sm">Company</Label>
                      <Input
                        id="company" data-testid="contact-input-company"
                        value={form.company} onChange={(e) => update("company", e.target.value)}
                        className="mt-1.5 h-11" placeholder="Company / Brand"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-sm">Service of interest</Label>
                      <Select value={form.service} onValueChange={(v) => update("service", v)}>
                        <SelectTrigger data-testid="contact-input-service" className="mt-1.5 h-11">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICE_OPTIONS.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="message" className="text-sm">How can we help? *</Label>
                      <Textarea
                        id="message" data-testid="contact-input-message"
                        value={form.message} onChange={(e) => update("message", e.target.value)}
                        className="mt-1.5 min-h-[140px]" placeholder="Tell us about your goals, current marketing, and what you'd like to achieve..."
                      />
                      {errors.message && <p className="mt-1 text-xs text-saavi-red">{errors.message}</p>}
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                    <p className="text-xs text-muted-foreground">By submitting, you agree to be contacted by Saavi Digitex.</p>
                    <Button
                      type="submit"
                      data-testid="contact-form-submit"
                      disabled={submitting}
                      className="h-12 px-7 gap-2"
                    >
                      {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Send Message <ArrowUpRight className="h-4 w-4" /></>}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </section>

      <section className="border-t border-border section-pad bg-secondary/30">
        <div className="container-saavi">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="font-mono-overline text-saavi-green mb-3">Or book directly</div>
              <h2 className="text-3xl md:text-4xl max-w-2xl">Pick a time that works for you.</h2>
              <p className="mt-3 text-muted-foreground max-w-xl">Skip the form — grab a free 30-minute slot on our calendar.</p>
            </div>
            <span className="font-mono-overline text-muted-foreground">Powered by Calendly</span>
          </div>
          <div data-testid="contact-calendly-section">
            <CalendlyEmbed />
          </div>
        </div>
      </section>
    </>
  );
}
